import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isPremium } from "@/components/PremiumGate";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

/**
 * Cultiv Morning Briefing — the daily editorial reading of the user's garden.
 *
 * Composed once per local day on first open. Premium only. Governed by
 * /app/CULTIV_LANGUAGE_SYSTEM.md §16 (Timeline Voice) — the AI is instructed
 * to vary its opening every day, never repeat itself, and cite only what the
 * timeline actually supports.
 */
export const MorningBriefing = () => {
    const { user } = useAuth();
    const premium = isPremium(user);
    const [briefing, setBriefing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!premium) { setLoading(false); return; }
        const tz = -new Date().getTimezoneOffset(); // minutes east of UTC
        (async () => {
            try {
                const { data } = await api.get("/morning-briefing", { params: { tz_offset: tz } });
                setBriefing(data);
            } catch {
                setError(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [premium]);

    if (!premium) {
        return (
            <section className="hairline rounded-lg p-8 md:p-10" data-testid="morning-briefing-free-teaser">
                <div className="eyebrow-gold mb-2 flex items-center gap-2">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Morning Briefing
                </div>
                <div className="font-serif text-2xl md:text-3xl leading-snug max-w-2xl">
                    A daily reading of your whole garden — composed for you, no two mornings alike.
                </div>
                <p className="mt-4 text-sm text-muted-foreground max-w-xl leading-relaxed">
                    Premium reads your last thirty days across every area — assessments, journals,
                    prayers, habits, and Tend reflections — and names what is quietly happening across
                    the garden entrusted to you. One honest observation, one biblical passage, one
                    faithful step for the day.{" "}
                    <Link to="/membership" className="text-[hsl(var(--eden-gold))] hover:underline">Continue cultivating &rarr;</Link>
                </p>
            </section>
        );
    }

    if (loading) {
        return (
            <section className="hairline rounded-lg p-10 md:p-14" data-testid="morning-briefing-loading">
                <div className="eyebrow-gold mb-3">This morning</div>
                <div className="font-serif text-3xl md:text-4xl leading-snug text-muted-foreground animate-pulse">
                    Considering the last thirty days…
                </div>
            </section>
        );
    }

    if (error || !briefing) {
        return null;
    }

    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden hairline rounded-lg p-10 md:p-14 bg-[hsl(var(--eden-forest-deep))] text-[hsl(var(--eden-cream))]"
            data-testid="morning-briefing"
        >
            <div className="absolute -right-16 -top-16 opacity-[0.06] pointer-events-none">
                <Sparkles className="w-96 h-96 text-[hsl(var(--eden-gold))]" strokeWidth={0.25} />
            </div>

            <div className="relative">
                <div className="eyebrow-gold mb-4">
                    This morning · {new Date().toLocaleDateString(undefined, { dateStyle: "long" })}
                </div>

                <div className="font-serif text-5xl md:text-6xl leading-[1.05] mb-10" data-testid="briefing-greeting">
                    {briefing.greeting}
                </div>

                <div className="max-w-3xl space-y-8">
                    <p className="prose-cultiv text-[hsl(var(--eden-cream))]/90" data-testid="briefing-observation">
                        {briefing.observation}
                    </p>

                    {briefing.pattern && (
                        <p className="font-serif text-2xl md:text-3xl leading-snug text-[hsl(var(--eden-cream))]" data-testid="briefing-pattern">
                            {briefing.pattern}
                        </p>
                    )}

                    {briefing.invitation && (
                        <div className="pt-6 border-t border-[hsl(var(--eden-cream))]/12" data-testid="briefing-invitation">
                            <div className="eyebrow-gold mb-3">The invitation</div>
                            <p className="font-serif text-xl md:text-2xl leading-snug italic">
                                {briefing.invitation}
                            </p>
                        </div>
                    )}

                    {briefing.scripture_verse && (
                        <div className="pt-6 border-t border-[hsl(var(--eden-cream))]/12" data-testid="briefing-scripture">
                            <div className="eyebrow-gold mb-3">Today's Scripture · {briefing.scripture_reference}</div>
                            <blockquote className="font-serif text-lg md:text-xl italic text-[hsl(var(--eden-cream))]/90 leading-relaxed">
                                &ldquo;{briefing.scripture_verse}&rdquo;
                            </blockquote>
                        </div>
                    )}

                    {briefing.faithful_step && (
                        <div className="pt-6 border-t border-[hsl(var(--eden-cream))]/12" data-testid="briefing-step">
                            <div className="eyebrow-gold mb-3">Today's faithful step</div>
                            <p className="font-serif text-xl md:text-2xl leading-snug">
                                {briefing.faithful_step}
                            </p>
                        </div>
                    )}
                </div>

                <div className="mt-10 pt-6 border-t border-[hsl(var(--eden-cream))]/12">
                    <Link
                        to="/patterns"
                        data-testid="briefing-see-patterns-link"
                        className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--eden-cream))]/60 hover:text-[hsl(var(--eden-gold))] transition-colors inline-flex items-center gap-2"
                    >
                        See the deeper patterns <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        </motion.section>
    );
};

export default MorningBriefing;
